import React, { useState, useEffect, useCallback } from 'react';
import { useWidget } from '../useWidget';
import { useDashboardLayout } from 'store/redux/states/dashboard/dashboard.hooks';
import Icon from '@mdi/react';
import { mdiTrashCanOutline, mdiFilter, mdiPaletteSwatch, mdiPlus, mdiReload } from '@mdi/js';
import { Button, Divider, Tabs, Dropdown, Flex } from 'antd';
import WidgetFilter from '../WidgetFilter';
import { useWidgetDate } from 'store/redux/states/dates/dates.hooks';
import Confirmation from 'components/Dialogs/Confirmation';
import Design from 'modules/DashboardModule/widgets/WidgetConfigurationModal/components/Design';
import Ninja from '@ninja';
import { snackbar } from 'components/notifications/notifications';

const WidgetConfiguration = ({ handleDelete, closePopover }) => {
  const {
    filters,
    setFilters,
    runFilter,
    default_filter,
    defaultFilter,
    setDefaultFilter,
    dashboard_widget_key,
    widget_key,
    disableDateFilter,
    position,
    index,
    chartConfig,
    editableStr,
    showCustomDesign,
  } = useWidget();
  const { config, updateWidget } = useDashboardLayout();
  const { setDate } = useWidgetDate(widget_key);
  const [activetab, setActivetab] = useState('filters');
  const {
    date_from: globalDateFrom,
    date_to: globalDateTo,
    date_range_key: globalKey,
  } = useWidgetDate('global');

  useEffect(() => {
    if (config[index]) {
      setDefaultFilter(filters);
    }
  }, [filters, config, index]);

  const items = [
    {
      key: 'filters',
      label: 'Filters',
      icon: <Icon path={mdiFilter} />,
      children: <WidgetFilter closePopover={closePopover} />,
    },
    ...(showCustomDesign || chartConfig?.load_chart
      ? [
          {
            key: 'design',
            label: 'Design',
            icon: <Icon path={mdiPaletteSwatch} size={1} />,
            children: (
              <div className='overflow-auto max-h-96'>
                <Design />
              </div>
            ),
          },
        ]
      : []),
  ];

  const createWidget = () => {
    const filteredFilters =
      disableDateFilter || !defaultFilter?.selected_period
        ? Object.fromEntries(
            Object.entries(defaultFilter).filter(
              ([key]) => !['date_from', 'date_to', 'date_range_key'].includes(key)
            )
          )
        : defaultFilter;

    const newConfig = {
      key: dashboard_widget_key,
      title: editableStr,
      position: position,
      defaultFilter: filteredFilters,
      show: true,
      config: chartConfig,
    };

    snackbar.success(`${editableStr} Widget added successfully`);
    updateWidget([newConfig, ...config]);
    closePopover();
  };

  const handleUpdateWidget = () => {
    const filteredFilters =
      disableDateFilter || !defaultFilter?.selected_period
        ? Object.fromEntries(
            Object.entries(defaultFilter).filter(
              ([key]) => !['date_from', 'date_to', 'date_range_key'].includes(key)
            )
          )
        : defaultFilter;

    const newConfig = {
      key: dashboard_widget_key,
      title: editableStr,
      position: position,
      defaultFilter: filteredFilters,
      show: true,
      config: chartConfig,
    };

    const updatedConfig = [...config];
    updatedConfig[index] = newConfig;

    updateWidget(updatedConfig);
    snackbar.success(`${editableStr} widget updated successfully`);
    closePopover();
  };

  const handleTypeChange = (key) => {
    setActivetab(key);
  };

  const resetFilter = useCallback(() => {
    setFilters({ ...default_filter });

    if (default_filter.date_from || default_filter.date_range_key) {
      setDate({
        date_from: default_filter.date_from,
        date_to: default_filter.date_to,
        date_range_key: default_filter.date_range_key,
      });
    } else {
      setDate({
        date_from: globalDateFrom,
        date_to: globalDateTo,
        date_range_key: globalKey,
      });
    }
    runFilter();
  }, [default_filter, setFilters, setDate, runFilter]);

  return (
    <div className='sm:max-w-[30rem] w-full'>
      <Tabs
        className='flex w-full'
        activeKey={activetab}
        items={items}
        onChange={handleTypeChange}
      />
      <Divider />

      <Flex gap='middle' justify='space-between' align='center' className='w-full'>
        <Confirmation
          type='warning'
          onConfirm={handleDelete}
          description='Are you sure you want to delete?'
          className='w-fit'
        >
          <Button shape='circle' type='text'>
            <Icon path={mdiTrashCanOutline} color={Ninja.colors.medium} size={1} />
          </Button>
        </Confirmation>
        <div className='flex items-center gap-2'>
          <Button
            icon={<img src='/icons/reset-orange.svg' className='w-3 h-3' />}
            onClick={resetFilter}
          >
            Reset
          </Button>
          <Dropdown.Button
            className='w-fit'
            onClick={() => setFilters({ ...defaultFilter })}
            type='primary'
            menu={{
              items: [
                {
                  label: <span className='text-sm'>Filter and save</span>,
                  key: 'update',
                  onClick: handleUpdateWidget,
                  icon: <Icon path={mdiReload} />,
                },
                {
                  label: <span className='text-sm'>Save as new widget</span>,
                  key: 'new_widget',
                  onClick: createWidget,
                  icon: <Icon path={mdiPlus} />,
                },
              ],
            }}
          >
            Filter
          </Dropdown.Button>
        </div>
      </Flex>
    </div>
  );
};

export default WidgetConfiguration;
